import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import {
  createDraft,
  restoreDraftForSession,
  type GuestSessionInfo,
  type ReportDraft,
} from './draft.js';

interface DraftContextValue {
  session: GuestSessionInfo | null;
  draft: ReportDraft | null;
  photo: File | null;
  loading: boolean;
  error: string | null;
  update: (updater: Partial<ReportDraft> | ((draft: ReportDraft) => ReportDraft)) => void;
  setPhoto: (photo: File | null) => void;
  reset: () => void;
}

const DraftContext = createContext<DraftContextValue | null>(null);
const STORAGE_KEY = 'refindi.voice.report.draft';

export function DraftProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<GuestSessionInfo | null>(null);
  const [draft, setDraft] = useState<ReportDraft | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api<GuestSessionInfo>('/api/guest/session')
      .then((guestSession) => {
        if (!active) return;
        setSession(guestSession);
        setDraft(restoreDraftForSession(readDraft(), guestSession));
      })
      .catch(() => active && setError('INVITATION_REQUIRED'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!draft) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  }, [draft]);

  const value = useMemo<DraftContextValue>(
    () => ({
      session,
      draft,
      photo,
      loading,
      error,
      update: (updater) =>
        setDraft((current) => {
          if (!current) return current;
          return typeof updater === 'function' ? updater(current) : { ...current, ...updater };
        }),
      setPhoto,
      reset: () => {
        if (!session) return;
        setPhoto(null);
        setDraft(createDraft({ ...session, language: draft?.language ?? session.language }));
      },
    }),
    [session, draft, photo, loading, error],
  );

  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
}

export function useDraft(): DraftContextValue {
  const context = useContext(DraftContext);
  if (!context) throw new Error('useDraft must be used inside DraftProvider');
  return context;
}

export function persistDraftForSession(guestSession: GuestSessionInfo): void {
  const next = restoreDraftForSession(readDraft(), guestSession);
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function readDraft(): ReportDraft | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value ? (JSON.parse(value) as ReportDraft) : null;
  } catch {
    return null;
  }
}
